import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../services/modal.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { I18nTranslator } from '../../i18n/translate';
import { Farm } from '../../models/farm.model';
import { Country } from '../../models/lists.model';
import { User } from '../../models/user.model';
import { FarmService } from '../../services/farm.service';
import { KeycloakService } from '../../services/keycloak.service';
import { ListsService } from '../../services/lists.service';
import { UserService } from '../../services/user.service';
import { PreventDoubleClickDirective } from '../../utils/preventDoubleClick';
import { isFarmerActionForbidden } from '../../utils/farmer-action-error';
import { showErrorMessage } from '../../utils/utils';
import { AutocompleteComponent } from '../../components/autocomplete/autocomplete.component';
import { TranslatePipe, TranslateWithPunctuationPipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'farm-edit',
  templateUrl: 'farm-edit.component.html',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    PreventDoubleClickDirective,
    TranslatePipe,
    TranslateWithPunctuationPipe,
    CommonModule,
    FormsModule,
    AutocompleteComponent,
  ],
  providers: [FarmService, UserService],
})
export class FarmEditComponent implements OnInit, OnDestroy {
  @Input() farm: Farm;
  @Output() close = new EventEmitter();
  error: any;
  sub: any;
  edited = false; // true if navigated here
  countries: Country[];
  farmId: number;

  isPartner = false;
  isAdmin: boolean;

  // Roles a farm can be owned by
  static readonly OWNER_ROLES = ['ROLE_FARMER', 'ROLE_ADVISOR', 'ROLE_MANAGER'];
  ownerText = '';
  readonly ownerLabel = (user: User) => [user.firstName, user.lastName].filter(Boolean).join(' ') || user.login;
  readonly ownerDetail = (user: User) => user.login;
  // Server-side search, 20 users at a time, instead of loading every user in scope
  readonly searchOwners = (term: string): Observable<User[]> =>
    this.findOwners(term, 20).pipe(map((page) => page.content));

  constructor(
    private farmService: FarmService,
    private listService: ListsService,
    private userService: UserService,
    private i18nTranslator: I18nTranslator,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
  }

  goBack(savedFarm: Farm = null) {
    this.close.emit(savedFarm);
    this.router.navigate(['/farms']);
    return false;
  }

  getCountries() {
    this.listService.getCountries().subscribe((countries) => (this.countries = countries));
  }

  findOwners(term: string, size: number) {
    return this.userService.getUsersAdvanced(
      1,
      size,
      'lastName',
      'asc',
      { filter: term, roles: FarmEditComponent.OWNER_ROLES },
      null,
    );
  }

  /** New farm: when a single user can own it, it is picked. */
  selectSingleOwner() {
    this.findOwners(null, 2).subscribe((page) => {
      if (!this.edited && page.totalElements === 1) {
        this.onOwnerSelected(page.content[0]);
      }
    });
  }

  onOwnerTextChange() {
    // Editing the text drops the previous owner until a suggestion is picked
    this.farm.user = new User();
    this.farm.user.id = null;
  }

  onOwnerSelected(user: User) {
    this.farm.user = user;
    this.ownerText = this.ownerLabel(user);
  }

  save() {
    this.farmService.save(this.farm).subscribe(
      (farm) => {
        this.farm = farm;
        this.goBack(farm);
      },
      (error) => {
        this.handleSaveError(error);
      },
    );
  }

  ngOnInit() {
    this.sub = this.route.params.subscribe((params) => {
      if (this.route.snapshot.url[1].path === 'edit') {
        this.farmId = +params['farmId'];
        this.edited = true;
        // FIXME traiter les erreurs
        this.farmService.getFarm(this.farmId).subscribe(
          (farm) => {
            this.farm = farm;
            if (!this.farm.country || !this.farm.country.id) {
              this.farm.country = new Country();
              this.farm.country.id = 1;
            }
            if (!this.farm.user || !this.farm.user.id) {
              this.farm.user = new User();
              this.farm.user.id = null;
            }
            if (this.farm.identifiantFms) {
              if (this.farm.identifiantFms.startsWith('WIUZ:') || this.farm.identifiantFms.startsWith('SMAG:')) {
                this.isPartner = true;
              }
            }
            this.ownerText = this.farm.user.id ? this.ownerLabel(this.farm.user) : '';
          },
          (error) => this.handleGetError(error),
        );
      } else if (this.route.snapshot.url[1].path === 'new') {
        this.edited = false;
        this.farm = new Farm();
        this.farm.country = new Country();
        // France par défaut
        this.farm.country.id = 1;
        this.farm.user = new User();
        this.farm.user.id = null;

        if (this.farm.identifiantFms) {
          if (this.farm.identifiantFms.startsWith('WIUZ:') || this.farm.identifiantFms.startsWith('SMAG:')) {
            this.isPartner = true;
          }
        }
        this.ownerText = '';
        this.selectSingleOwner();
      }
    });

    this.getCountries();
  }

  handleSaveError(error: any) {
    if (isFarmerActionForbidden(error)) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.farmerActionForbidden'));
    } else if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('farm.edit.unauthorized'));
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('edit.error.generic'));
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('farm.edit.view.unauthorized')).then((r) => {
        this.router.navigate(['/farms']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.generic')).then((r) => {
        this.router.navigate(['/farms']);
      });
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
